import { MAX_THREAD_MESSAGE, type MessageDto } from '@eventping/shared'
import { Check, MessageSquareText, Send } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'
import { timeOf } from './shell'

/**
 * The event's conversation: messages written on the page, newest at the bottom. Each step
 * marked done is a system entry (a check, its title, the time), its note follows as a message.
 */
export function Thread({
  messages,
  viewer,
  onSend,
  disabled,
  placeholder = 'Write a message to everyone',
  emptyText = 'No messages yet.',
}: {
  messages: MessageDto[]
  /** Whose messages sit on the right: the creator's, or the viewing contact's. */
  viewer: 'creator' | 'contact'
  onSend?: (body: string) => Promise<void>
  disabled?: boolean
  placeholder?: string
  emptyText?: string
}) {
  const [draft, setDraft] = useState('')
  const [sending, setSending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const end = useRef<HTMLDivElement>(null)
  // Scroll to the newest message only after the viewer sends one: never on load (a contact
  // opens the page to see the timeline first) and never under someone who is reading.
  const justSent = useRef(false)
  const count = messages.length

  useEffect(() => {
    if (count > 0 && justSent.current) {
      justSent.current = false
      end.current?.scrollIntoView({ block: 'nearest' })
    }
  }, [count])

  async function send() {
    const body = draft.trim()
    if (!body || !onSend) return
    setSending(true)
    setError(null)
    try {
      justSent.current = true
      await onSend(body)
      setDraft('')
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setSending(false)
    }
  }

  return (
    <div>
      <ol className="space-y-3" aria-label="Messages">
        {messages.length === 0 && (
          <li className="flex items-center gap-2 rounded-xl bg-muted px-4 py-3 text-muted-foreground">
            <MessageSquareText className="size-5" aria-hidden />
            {emptyText}
          </li>
        )}
        {messages.map((m) => {
          if (m.kind === 'step') {
            return (
              <li
                key={m.id}
                className="flex items-center gap-3 py-1 text-sm text-muted-foreground"
                data-testid="thread-step"
              >
                <span className="h-px flex-1 bg-border" aria-hidden />
                <span className="inline-flex min-w-0 items-center gap-1.5 rounded-full bg-done-soft px-3 py-1 text-foreground">
                  <span className="grid size-4 shrink-0 place-items-center rounded-full bg-done text-white dark:text-background">
                    <Check className="size-3" aria-hidden />
                  </span>
                  <span className="sr-only">Step done: </span>
                  <span className="truncate font-medium">{m.body}</span>
                  <span className="shrink-0 text-muted-foreground">· {timeOf(m.createdAt)}</span>
                </span>
                <span className="h-px flex-1 bg-border" aria-hidden />
              </li>
            )
          }
          const right = viewer === 'creator' ? m.author === 'creator' : m.mine
          return (
            <li key={m.id} className={cn('flex flex-col', right ? 'items-end' : 'items-start')}>
              <div className="mb-1 px-1 text-sm text-muted-foreground">
                <span className="font-medium text-foreground">
                  {right && viewer === 'contact' ? 'You' : m.name}
                </span>
                {' · '}
                {timeOf(m.createdAt)}
              </div>
              <p
                className={cn(
                  'max-w-[85%] rounded-2xl px-4 py-2.5 break-words whitespace-pre-wrap',
                  right
                    ? 'rounded-br-md bg-primary text-primary-foreground'
                    : 'rounded-bl-md bg-secondary text-secondary-foreground',
                )}
              >
                {m.body}
              </p>
            </li>
          )
        })}
      </ol>
      <div ref={end} />
      {onSend && (
        <form
          className="mt-4 flex items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault()
            void send()
          }}
        >
          <label className="sr-only" htmlFor="thread-draft">
            {placeholder}
          </label>
          <Textarea
            id="thread-draft"
            value={draft}
            maxLength={MAX_THREAD_MESSAGE}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={placeholder}
            disabled={disabled}
            className="min-h-12 resize-none"
            rows={1}
          />
          <Button
            type="submit"
            size="icon"
            disabled={disabled || sending || !draft.trim()}
            aria-label="Send message"
          >
            <Send />
          </Button>
        </form>
      )}
      {error && <p className="mt-2 text-sm text-destructive">{error}</p>}
    </div>
  )
}
